/* eslint-disable no-shadow */
import {
  GetterTree, MutationTree, ActionTree, Module
} from 'vuex';

import CustomerType from '@/enums/CustomerType';
import ExtraData from '@/models/ExtraData';
import { reCaptchaService } from '@/services/ReCaptchaService';
import VehicleCondition from '@/enums/VehicleCondition';
import KeyValue from '@/models/KeyValue';
import { EventTypeEnum, TrackingService } from '@/services/TrackingService';
import IRootState from '../states/interfaces/index.d';
import IEnquiryState from '../states/interfaces/enquiry-state.d';
import EnquiryState from '../states/enquiry-state';
import PurchaseTime from '../../enums/PurchaseTime';
import FormSubmissionState from '../../enums/FormSubmissionState';
import OptInData from '../../models/OptInData';
import IEnquiryChoices from '../../models/interfaces/IEnquiryChoices';
import Range from '../../models/vehicle/range';

export const state: IEnquiryState = new EnquiryState();

export const getters: GetterTree<IEnquiryState, IRootState> = {
  showEnterPostcodeManually: (state) => state.showEnterPostcodeManually,
  getEnquiryId: (state) => state.enquiry.enquiryId,
  testDriveRequested: (state) => state.enquiry.testDriveRequested,
  quoteRequested: (state) => state.enquiry.quoteRequested,
  brochureRequested: (state) => state.enquiry.brochureRequested,
  digitalBrochureRequested: (state) => state.enquiry.digitalBrochureRequested,
  motabilityRequested: (state) => state.enquiry.motabilityRequested,
  purchaseTime: (state) => state.enquiry.purchaseTime,
  enquirySubmissionState: (state) => state.enquirySubmissionState,
  enquiryChoicesContainsErrors: (state) => !state.enquiry.brochureRequested
    && !state.enquiry.quoteRequested
    && !state.enquiry.testDriveRequested
    && !state.enquiry.motabilityRequested,
  currentStep: (state) => state.currentStep,
  interactedSteps: (state): number => state.interactedSteps,
  skippedStepIds: (state): number[] => state.skippedStepIds,
  nextButtonsClicked: (state): string[] => state.nextButtonsClicked,
  getSimilarCarInterest: (state) => state.similarCarInterest,
  optInData: (state) => state.optInData,
  currentOverlayStep: (state): number => state.overlayStep,
  extraData: (state) => state.enquiry.extraData,
  customerType: (state) => state.enquiry.customerType,
  reCaptchaToken: (state): string | null => state.enquiry.reCaptchaToken,
  vehicleCondition: (state): VehicleCondition => {
    const extra = state.enquiry.extraData.find((x) => x.keyName === 'newUsedVehicle');

    if (extra) {
      return VehicleCondition[extra.keyValue];
    }

    return VehicleCondition.NotSet;
  },
  notes: (state): KeyValue[] => state.notes
};

export const mutations: MutationTree<IEnquiryState> = {
  setBrochureChecked: (state: EnquiryState, brochureChecked: boolean) => {
    state.enquiry.brochureRequested = brochureChecked;
  },
  setQuoteChecked: (state: EnquiryState, quoteChecked: boolean) => {
    state.enquiry.quoteRequested = quoteChecked;
  },
  setTestDriveChecked: (state: EnquiryState, testDriveChecked: boolean) => {
    state.enquiry.testDriveRequested = testDriveChecked;
  },
  setDigitalBrochureChecked: (state: EnquiryState, digitalBrochureEnquiry: boolean) => {
    state.enquiry.digitalBrochureRequested = digitalBrochureEnquiry;
  },
  setMotabilityChecked: (state: EnquiryState, motabilityEnquiry: boolean) => {
    state.enquiry.motabilityRequested = motabilityEnquiry;
  },
  setEnquirySubmissionState: (state: EnquiryState, submissionState: FormSubmissionState) => {
    state.enquirySubmissionState = submissionState;
  },
  setShowEnterPostcodeManually: (state: EnquiryState, manualPostcode: boolean) => {
    state.showEnterPostcodeManually = manualPostcode;
  },
  setPurchaseTime: (state: EnquiryState, purchaseTime: PurchaseTime) => {
    state.enquiry.purchaseTime = purchaseTime;
  },
  setOptInConsent: (state: EnquiryState, optInData: OptInData) => {
    const index = state.optInData.findIndex((e) => e.PolicyType === optInData.PolicyType);

    if (index === -1) {
      state.optInData.push(optInData);
    } else {
      state.optInData[index] = optInData;
    }
  },
  setSimilarCarInterest: (state: EnquiryState, similarCarInterest: string) => {
    state.similarCarInterest = similarCarInterest;
  },
  setCurrentStep: (state: EnquiryState, currentStep: number) => {
    state.currentStep = currentStep;
  },
  setInteractedSteps: (state: EnquiryState, currentStep: number) => {
    state.interactedSteps = currentStep;
  },
  setSkippedStepId: (state: EnquiryState, stepId: number) => {
    const exists = state.skippedStepIds.find((x: number) => x === stepId);

    if (!exists) {
      state.skippedStepIds.push(stepId);
    }
  },
  setRemoveSkippedStepId: (state: EnquiryState, stepId: number) => {
    const steps = state.skippedStepIds.filter((x: number) => x !== stepId);

    if (steps) {
      state.skippedStepIds = steps;
    }
  },
  setRemoveAllSkippedSteps: (state: EnquiryState) => {
    state.skippedStepIds = [];
  },
  setNextStepClicked: (state: EnquiryState, validationScope: string) => {
    state.nextButtonsClicked.push(validationScope);
  },
  setResetNextStepClicked: (state: EnquiryState) => {
    state.nextButtonsClicked = [];
  },
  setOverlayStep: (state: EnquiryState, nextStep: number) => {
    state.overlayStep = nextStep;
  },
  setCustomerType: (state: EnquiryState, customerType: CustomerType) => {
    state.enquiry.customerType = customerType;
  },
  setExtraData: (state: EnquiryState, extraData: ExtraData) => {
    const currentExtraData = state.enquiry.extraData.find((x: ExtraData) => x.keyName === extraData.keyName);
    if (currentExtraData) {
      currentExtraData.keyValue = extraData.keyValue;
    } else {
      state.enquiry.extraData.push(extraData);
    }
  },
  setResetExtraData: (state: EnquiryState) => {
    state.enquiry.extraData = [];
  },
  setRemoveExtraData: (state: EnquiryState, extraDataKey: string) => {
    const currentExtraData = state.enquiry.extraData.filter((x: ExtraData) => x.keyName !== extraDataKey);
    if (currentExtraData) {
      state.enquiry.extraData = currentExtraData;
    }
  },
  setReCaptchaToken: (state: EnquiryState, reCaptchaToken: string | null) => {
    state.enquiry.reCaptchaToken = reCaptchaToken;
  },
  setNote: (state: EnquiryState, note: KeyValue) => {
    const currentNote = state.notes.find((x: KeyValue) => x.key === note.key);
    if (currentNote) {
      currentNote.value = note.value;
    } else {
      state.notes.push(note);
    }
  }
};

export const actions: ActionTree<IEnquiryState, IRootState> = {
  async submitFormReset({ dispatch, commit, rootState }) {
    await dispatch('vehicleModule/submitManufacturer', null, { root: true });
    await dispatch('initialSettingsModule/submitInitialSettings', rootState.initialSettingsModule.initialSettings, { root: true });
    await dispatch('vehicleModule/submitResetVehicleCustomisationData', null, { root: true });
    await dispatch('vehicleSearchModule/updateManufacturers', null, { root: true });

    dispatch('vehicleManagementModule/submitResetModuleInstances', null, { root: true });
    dispatch('vehicleManagementModule/submitResetExtraVehicles', null, { root: true });
    dispatch('vehicleValuationModule/submitResetValuationData', null, { root: true });
    dispatch('financeModule/submitResetPurchaseTypeGroupFields', null, { root: true });
    dispatch('businessModule/submitResetBusinessValues', null, { root: true });
    dispatch('personalDetailsModule/submitIsPrePhoneEntry', false, { root: true });

    commit('setResetNextStepClicked');
    commit('setResetExtraData');
    commit('setRemoveAllSkippedSteps');
    commit('setOverlayStep', 1);
    commit('setInteractedSteps', 1);
    commit('setCurrentStep', 1);
  },
  submitEnquiryChoices({ commit }, enquiryChoices: IEnquiryChoices) {
    if (enquiryChoices.isBasicEnquiry) {
      enquiryChoices.isBrochureEnquiry = false;
      enquiryChoices.isQuoteEnquiry = false;
      enquiryChoices.isTestDriveEnquiry = false;
      enquiryChoices.isDigitalBrochureEnquiry = false;
    } else if (!enquiryChoices.isBrochureEnquiry
      && !enquiryChoices.isQuoteEnquiry
      && !enquiryChoices.isTestDriveEnquiry
      && !enquiryChoices.isDigitalBrochureEnquiry
      && !enquiryChoices.isMotabilityEnquiry
    ) {
      enquiryChoices.isBrochureEnquiry = true;
      enquiryChoices.isQuoteEnquiry = true;
      enquiryChoices.isTestDriveEnquiry = true;
      enquiryChoices.isDigitalBrochureEnquiry = false;
    }

    commit('setBrochureChecked', enquiryChoices.isBrochureEnquiry);
    commit('setQuoteChecked', enquiryChoices.isQuoteEnquiry);
    commit('setTestDriveChecked', enquiryChoices.isTestDriveEnquiry);
    commit('setDigitalBrochureChecked', enquiryChoices.isDigitalBrochureEnquiry);
    commit('setMotabilityChecked', enquiryChoices.isMotabilityEnquiry);
  },
  submitBrochureChecked({ commit }, brochureChecked: boolean) {
    commit('setBrochureChecked', brochureChecked);
  },
  submitQuoteChecked({ commit }, quoteChecked: boolean) {
    commit('setQuoteChecked', quoteChecked);
  },
  submitTestDriveChecked({ commit }, testDriveChecked: boolean) {
    commit('setTestDriveChecked', testDriveChecked);
  },
  submitShowEnterPostcodeManually({ commit }, manualPostcode: boolean) {
    commit('setShowEnterPostcodeManually', manualPostcode);
  },
  submitFormSubmissionState({ commit }, formSubmissionState: FormSubmissionState) {
    commit('setEnquirySubmissionState', formSubmissionState);
  },
  submitPurchaseTime({ commit }, purchaseTime: PurchaseTime) {
    commit('setPurchaseTime', purchaseTime);
  },
  submitOptInConsent({ commit }, optinConsent: OptInData) {
    commit('setOptInConsent', optinConsent);
  },
  submitSimilarCarInterest: ({ commit }, similarCarInterest: string) => {
    commit('setSimilarCarInterest', similarCarInterest);
  },
  submitNextStep: ({
    dispatch, commit, state, rootGetters
  }, nextStep: number) => {
    if (state.skippedStepIds.find((x) => x === nextStep)) {
      const isBack = state.currentStep > nextStep;
      nextStep = isBack ? nextStep - 1 : nextStep + 1;
    }
    const nextStepTest = nextStep;
    const currentStepTest = state.currentStep;
    const { interactedSteps } = state;
    commit('setCurrentStep', nextStep);

    if (nextStep > interactedSteps) {
      commit('setInteractedSteps', nextStep);
    }

    const { totalSteps } = rootGetters['initialSettingsModule/initialSettings'];

    TrackingService.instance.sendEvent(
      EventTypeEnum.StepChange,
      rootGetters['initialSettingsModule/correlationId'],
      {
        fromStep: currentStepTest,
        toStep: nextStepTest,
        totalSteps
      }
    );
  },
  submitSkippedStepId: ({ commit }, step: number) => {
    commit('setSkippedStepId', step);
  },
  submitRemoveSkippedStepId: ({ commit }, step: number) => {
    commit('setRemoveSkippedStepId', step);
  },
  submitNextStepClicked: ({ commit }, validationScope: string) => {
    commit('setNextStepClicked', validationScope);
  },
  submitOverlayStep: ({ commit }, nextStep: number) => {
    commit('setOverlayStep', nextStep);
  },
  submitCustomerType: ({ commit }, customerType: CustomerType) => {
    commit('setCustomerType', customerType);
  },
  submitExtraData: ({ commit }, extraData: ExtraData) => {
    commit('setExtraData', extraData);
  },
  submitRemoveExtraData: ({ commit }, extraDataKey: string) => {
    commit('setRemoveExtraData', extraDataKey);
  },
  async submitReCaptchaToken({ commit }) {
    await reCaptchaService.retrieveReCaptchaToken()
      .then((token: string | null) => {
        commit('setReCaptchaToken', token);
      });
  },
  submitNote: ({ commit }, note: KeyValue) => {
    commit('setNote', note);
  }
};

export const enquiryModule: Module<IEnquiryState, IRootState> = {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
